import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { on, removeListener, sendMessageData } from "api/prokey/prokey.websocket";
import {useMouseController} from "../temp/useMouseController";
import {useImage} from "../ImageContext/ImageContext";

//-----------------------------------------------------------------
export function Highlights(props) {
  //-----------------------------------------------------------------

  //#region [VALUES] 

  const {
    settings,
    is_comp,
  } = props;

  const { is_full_size, is_magnifier_held } = settings || {};
  const { width, height, source, image } = useImage();

  const [ ref_highlights     , setRefHighlights     ] = useState( null );
  const [ context_highlights , setContextHighlights ] = useState( null );

  const [ width_conversion  , setConversionWidth  ] = useState( 1 );
  const [ height_conversion , setConversionHeight ] = useState( 1 );

  const [ canvas_highlights , setCanvasHighlights ] = useState( {} );
  
  const [ fields, setFields ] = useState( null );
  const [ field , setField  ] = useState( null );

  const pointer = useMouseController({
    target: {current: ref_highlights},
    // Listen on window, compute relative against overlayRef
    listenOn: "document",
    rafThrottle: true,
    includeGlobalCoords: false,
  });

  const setHighlightsRefNode = useCallback(node => {
    if (node !== null) {
      const context = node.getContext("2d")
      setContextHighlights( context );
      setRefHighlights( node );
    }
  }, []);

  let selected_entry = null;

  const ref_coord_start = useRef(null)
  const ref_coord_end   = useRef(null)

  const drawNew    = useCallback( _draw, [context_highlights, pointer, image] );
  const drawFields = useCallback( drawFieldHighlights, [context_highlights, canvas_highlights, image] );

  //#endregion

  //#region [STYLES]
  
  const fit_page = {
    maxWidth  : '100%',
    maxHeight : '100%',
  }

  const fit_width = {
    width  : '100%',
    height : '',
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFields(data) {
      //-----------------------------------------------------------------
      setFields( data );
    }

    const type = is_comp ? 'FILE_COMPARISON_IMAGE_FIELDS' : 'FILE_IMAGE_FIELDS';
    on( type, handleFields );

    return () => {
      removeListener( type, handleFields );
    }
  }, [] );

  useEffect( () => {
    createFieldHighlights();
  }, [fields, image] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleField(data) {
      //-----------------------------------------------------------------
      if( !data.is_selected ) {
        return;
      }
      setField( data );
    }

    const type = is_comp ? 'FILE_COMPARISON_FIELD' : 'FILE_FIELD';
    on( type, handleField );

    return () => {
      removeListener( type, handleField );
    }
  }, [] );

  useEffect( () => {
    createFieldHighlights();
  }, [fields, image, field] );
  
  useEffect(() => {
    if( context_highlights == null ) return;
    drawNew();
  }, [context_highlights, pointer, image]);
  
  useEffect(() => {
    drawFields();
  }, [canvas_highlights, context_highlights, image]);

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function getHighlightRect(coords) {
    //-----------------------------------------------------------------
    const skewedTopLeft     = skew( coords.left , coords.top    );
    const skewedBottomRight = skew( coords.right, coords.bottom );

    const skewedX      = parseInt( skewedTopLeft.x     *  width_conversion );
    const skewedY      = parseInt( skewedTopLeft.y     * height_conversion );
    const skewedWidth  = parseInt( skewedBottomRight.x *  width_conversion ) - skewedX;
    const skewedHeight = parseInt( skewedBottomRight.y * height_conversion ) - skewedY;
    
    return { x: skewedX, y: skewedY, width: skewedWidth, height: skewedHeight};
  }

  //-----------------------------------------------------------------
  function skew(x,y) {
    //-----------------------------------------------------------------
    let result = { x: parseInt(x), y: parseInt(y) };
    const slope = Math.tan( (source.deskew_alpha*Math.PI)/180.0 );

    if( source.deskew_mode === '1' ) {
      // calculate x
      const y_start = slope > 0 ? 0 : source.deskew_height;
      const x_offset = slope * (y - y_start);
      const x_change = (Math.round(x_offset + 0.5)); // truncate decimal
      result.x -= x_change;
    }

    const y_change = ((slope * result.x + 0.5) << 0);
    result.y += y_change;

    return result;
  }

  //-----------------------------------------------------------------
  function createFieldHighlights() {
    //-----------------------------------------------------------------
    let _fields = [field];
    if( _fields?.length <= 0 ) {
      setCanvasHighlights({});
    }
    
    if( !image ) return;

    const canvas_buffers = {};

    for( const field of _fields ) {
      if( field == null ) continue;
      const is_current = field.is_selected;

      const field_buffer = { id: field.id, entries: new Map(), is_current }

      canvas_buffers[ field.id ] = field_buffer;

      if( field?.image?.id !== source?.id ) continue;

      let coords_temp = {
        left   : field.Entries[0].Left,
        top    : field.Entries[0].Top,
        right  : field.Entries[0].Right,
        bottom : field.Entries[0].Bottom,
      }
      const coords = getHighlightRect(coords_temp);
      const canvas = createFieldHighlight( coords, is_current, false );
      const buffer = { canvas, x: coords.x, y: coords.y, is_selected: false };
      field_buffer.entries.set( coords, buffer );
    }

    setCanvasHighlights( canvas_buffers );
  }
  
  //-----------------------------------------------------------------
  function createFieldHighlight( { x, y, width, height }, is_curr_field, is_selected) {
    //-----------------------------------------------------------------
    const canvas = document.createElement('canvas')
    canvas.width  = width  + 4 ;
    canvas.height = height + 4 ;

    const buffer_context = canvas.getContext('2d');
    buffer_context.drawImage( image, x, y, width, height, 0, 0, width, height );

    const color = is_curr_field ? 'rgb( 255, 255, 0 )' : 'rgb( 128, 255, 0 )'

    buffer_context.globalCompositeOperation = "darken";

    buffer_context.beginPath();

    if( is_curr_field && is_selected ) {
      buffer_context.lineWidth = 3;
      buffer_context.strokeStyle = "black";
      buffer_context.strokeRect( 2, 2, width, height);
    }

    buffer_context.fillStyle = color;
    buffer_context.fillRect(0, 0, width, height);

    buffer_context.closePath();

    return canvas;
  }

  //-----------------------------------------------------------------
  function _draw() {
    //-----------------------------------------------------------------
    if( !image ) return;
    if( !context_highlights ) return;
    if( width <= 1) return;

    if ( pointer.secondaryDown && ref_coord_start.current === null ) {
      // highlightCoordsStart();
    }
    else if ( pointer.secondaryDown ) {
      // highlightCoordsEnd();
    }
    
    if ( pointer.secondaryDown && ref_coord_end.current !== null ) {
      console.info('draw curr highlight' )
      // drawCurrentHighlight();
    }
    else if( pointer.secondaryDown === false && ref_coord_end.current !== null ) {
      // saveHighlightCoords();
      drawFieldHighlights();
    }
  }

  //-----------------------------------------------------------------
  function drawFieldHighlights() {
    //-----------------------------------------------------------------
    if( !context_highlights || !image ) return;

    context_highlights.clearRect(0, 0, width, height);
    Object.values( canvas_highlights ).forEach( _field => {
      _field.entries.forEach( (highlight) => {
        if( highlight.canvas.width > 0 && highlight.canvas.height > 0 ) {
          context_highlights.drawImage( highlight.canvas, highlight.x, highlight.y );
        }
      })
    })
  }

  //#endregion

  //#region [EVENTS]


  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( width == null ) return null
    
    return (
      <canvas
        ref    = { setHighlightsRefNode }
        width  = {width }
        height = {height}
        style  ={ {
          position  : 'absolute' ,
          top       : 0          ,
          left      : 0          ,
          zIndex    : 25         ,
          ...(is_full_size ? fit_width : fit_page),
          // No pointer events; canvas under it remains interactive
          pointerEvents: "none",
        } }
      />
    );
  }

  //#endregion

  return display();
};